/**
 * @file Logo.jsx
 * @description ABAcases logo. The mark (briefcase + A) is a vector asset; the
 *   "ABAcases" wordmark is set in the app's Inter font — "ABA" at 700 and
 *   "cases" at 400 — so it stays crisp at any size, is weight-controllable, and
 *   matches the rest of the UI's typography (the production outlined wordmark
 *   used a much heavier "ABA" that read as too bold).
 *
 *   Variants:
 *     - "mark"    → the briefcase+A mark only (square) — the default
 *     - "lockup"  → mark + wordmark, side by side (horizontal)
 *     - "stacked" → mark above the wordmark (vertical) — used on the auth hero
 * @module components/Logo
 */

const NAVY = "#002868";
const MARK_SRC = "/ABAcases-mark.svg";

/** The "ABAcases" wordmark in Inter — ABA bold (700), cases regular (400). */
function Wordmark({ size, style }) {
  return (
    <span
      style={{
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        fontSize: size,
        lineHeight: 1,
        color: NAVY,
        letterSpacing: "-0.02em",
        whiteSpace: "nowrap",
        userSelect: "none",
        ...style,
      }}
    >
      <span style={{ fontWeight: 700 }}>ABA</span>
      <span style={{ fontWeight: 400 }}>cases</span>
    </span>
  );
}

export default function Logo({ variant = "mark", height = 32, alt = "ABAcases", onClick, style }) {
  const clickStyle = onClick ? { cursor: "pointer" } : {};

  // Composed lockups: mark image + live Inter wordmark. `height` = mark height.
  if (variant === "lockup" || variant === "stacked") {
    const stacked = variant === "stacked";
    // The briefcase handle inflates the mark's bounding box at the top, so plain
    // geometric centering makes the wordmark read as too high. In the horizontal
    // lockup, nudge the wordmark down onto the case body's optical center
    // (~0.575 of mark height). Not needed when stacked (wordmark is below).
    const wordNudge = stacked ? 0 : Math.round(height * 0.075);
    return (
      <div
        onClick={onClick}
        style={{
          display: "inline-flex",
          flexDirection: stacked ? "column" : "row",
          alignItems: "center",
          gap: stacked ? height * 0.15 : height * 0.28,
          ...clickStyle,
          ...style,
        }}
      >
        <img src={MARK_SRC} alt={alt} style={{ height, width: "auto", display: "block" }} />
        <Wordmark
          size={stacked ? height * 0.42 : height * 0.66}
          style={wordNudge ? { position: "relative", top: wordNudge } : undefined}
        />
      </div>
    );
  }

  // "mark" (and any fallthrough) → the standalone mark image
  return (
    <img
      src={MARK_SRC}
      alt={alt}
      onClick={onClick}
      style={{ height, width: "auto", objectFit: "contain", display: "block", ...clickStyle, ...style }}
    />
  );
}
